/**
 * Whether the platform's input method owns this keystroke.
 *
 * The 2014 client draws its own conversion-candidate window and registers one
 * per code page -- `VerticalCandidateBoard` for 932/936/950 and
 * `KORCandidateWindow` for 949 (`uicandidate.py:149-152`) -- because a
 * fullscreen DirectX window cannot show the one the OS draws. While that window
 * is up the engine routes keys into `ime.*` and the candidate list, never into
 * the editor's own handler: `ui.py:653-665` opens and closes it, `ui.py:746-771`
 * is the composition-time key handler, and the arrow keys move the selection
 * `ime.GetCandidateSelection()` reports (`uicandidate.py:96,124`). A browser
 * draws that window itself, so what this build owes the capability is the same
 * yielding -- a panel that eats Enter, Escape or the arrows mid-composition
 * takes the keys that move and commit the candidate.
 */
export function composing(event: KeyboardEvent): boolean {
  // `keyCode === 229` is the same fact from an engine that reports the
  // composition keystroke without setting `isComposing`.
  return event.isComposing === true || event.keyCode === 229;
}

/**
 * Stand down from a keystroke the candidate window owns, and say so.
 *
 * Enter also takes `preventDefault()`: it commits the candidate at the OS
 * level either way, and without this the browser's implicit form submission
 * would send the half-typed line the composition is still building.
 */
export function yieldToCandidateWindow(event: KeyboardEvent): boolean {
  if (!composing(event)) return false;
  if (event.key === "Enter") event.preventDefault();
  return true;
}

/** Every `<input>` type the 2014 `EditLine` stands for. A checkbox or a button
 *  is not one, and a Return there is still the chat box's. */
const EDIT_LINE_TYPES: ReadonlySet<string> = new Set([
  "text", "password", "search", "email", "number", "tel", "url",
]);

/**
 * Whether a focused edit line already owns this Return.
 *
 * `CWindowManager::RunIMEReturnEvent` offers Return to the active window and
 * stops there once it answers (`PythonWindowManager.cpp:1077-1083`), so a
 * focused `EditLine` keeps `game.py:1551-1556` -- the Return that opens the
 * chat box -- from opening it over the field being typed into.
 */
export function editLineOwnsReturn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return EDIT_LINE_TYPES.has(target.type);
  return target.getAttribute("contenteditable") === "true";
}
